/**
 * EditUserPage Component
 * Full page for editing user details and permissions
 * Uses OathUI components for consistent styling
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  UserIcon,
  CheckIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShieldCheckIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import { getUserDetails, updateUser, getAvailablePermissions, getAvailableProducers, resendWelcomeEmail } from '../../../services/oathUserService';
import { useOathAuth } from '../../../services/oathAuthContext';
import { toast } from 'react-toastify';
import PermissionSelector from './PermissionSelector';
import AdminTabNavigation from './AdminTabNavigation';
import { formatPhoneNumber, stripPhoneFormatting } from './adminUtils';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathDualSpinner,
} from '../../shared/OathUI';

const EditUserPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useOathAuth();

  // Get agencyId from URL search params (set by AdminPage) or fallback to first agency
  const agencyId = searchParams.get('agencyId') || currentUser?.agencies?.[0]?.agencyId;

  // Get tab from URL, default to 'details'
  const activeTab = searchParams.get('tab') || 'details';
  const setActiveTab = (tab) => {
    const params = { tab };
    if (agencyId) params.agencyId = agencyId;
    setSearchParams(params, { replace: true });
  };

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    isActive: true,
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [inheritedPermissionIds, setInheritedPermissionIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [availableProducers, setAvailableProducers] = useState([]);
  const [selectedProducerId, setSelectedProducerId] = useState('');

  // Track initial values to detect changes
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [initialProducerId, setInitialProducerId] = useState('');

  const isEditingSelf = currentUser?.portalUserId === parseInt(userId);
  const fetchedRef = useRef(false);

  // Check if any changes have been made
  const hasChanges = initialFormData && (
    formData.firstName !== initialFormData.firstName ||
    formData.lastName !== initialFormData.lastName ||
    formData.phoneNumber !== initialFormData.phoneNumber ||
    formData.isActive !== initialFormData.isActive ||
    JSON.stringify([...selectedPermissions].sort()) !== JSON.stringify([...initialPermissions].sort()) ||
    selectedProducerId !== initialProducerId
  );

  // Fetch user details and available permissions
  useEffect(() => {
    const fetchData = async () => {
      if (!userId || !agencyId || fetchedRef.current) return;

      setLoading(true);
      setLoadError(null);
      fetchedRef.current = true;

      const [userResult, permissionsResult, producersResult] = await Promise.all([
        getUserDetails(agencyId, userId),
        getAvailablePermissions(agencyId),
        getAvailableProducers(agencyId),
      ]);

      if (userResult.success) {
        const user = userResult.user;
        setUserData(user);
        const loadedFormData = {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          phoneNumber: formatPhoneNumber(user.phoneNumber || ''),
          isActive: user.isActive ?? true,
        };
        const loadedPermissions = user.permissions?.map(p => p.portalPermissionId) || [];
        const inherited = user.permissions?.filter(p => p.isInherited).map(p => p.portalPermissionId) || [];
        const loadedProducerId = user.producerId ? String(user.producerId) : '';
        setFormData(loadedFormData);
        setSelectedPermissions(loadedPermissions);
        setInheritedPermissionIds(inherited);
        setSelectedProducerId(loadedProducerId);
        // Store initial values to detect changes
        setInitialFormData(loadedFormData);
        setInitialPermissions(loadedPermissions);
        setInitialProducerId(loadedProducerId);
      } else {
        setLoadError('Failed to load user details');
        toast.error('Failed to load user details');
      }

      if (permissionsResult.success) {
        setAvailablePermissions(permissionsResult.permissions || []);
      }

      if (producersResult.success) {
        setAvailableProducers(producersResult.producers || []);
      }

      setLoading(false);
    };

    fetchData();
  }, [userId, agencyId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;

    // Auto-format phone number
    if (name === 'phoneNumber') {
      processedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (isEditingSelf && !formData.isActive) {
      newErrors.isActive = 'You cannot deactivate your own account';
    }

    if (isEditingSelf && selectedPermissions.length === 0) {
      newErrors.permissions = 'You cannot remove all your own permissions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    const updateData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      phoneNumber: stripPhoneFormatting(formData.phoneNumber) || null,
      isActive: formData.isActive,
      permissionIds: selectedPermissions.filter(id => !inheritedPermissionIds.includes(id)),
      producerId: selectedProducerId && !userData?.producerId ? parseInt(selectedProducerId, 10) : undefined,
    };

    const result = await updateUser(agencyId, userId, updateData);

    if (result.success) {
      toast.success('User updated successfully');
      navigate(`/app/admin?agencyId=${agencyId}&tab=users`);
    } else {
      toast.error(result.error || 'Failed to update user');
    }

    setIsSubmitting(false);
  };

  const handleBack = () => {
    navigate(`/app/admin?agencyId=${agencyId}&tab=users`);
  };

  const handleResendWelcome = async () => {
    setIsResending(true);
    const result = await resendWelcomeEmail(agencyId, userId);
    if (result.success) {
      toast.success('Welcome email has been resent');
    } else {
      toast.error(result.error || 'Failed to resend welcome email');
    }
    setIsResending(false);
  };

  if (loading) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <div className="flex items-center justify-center min-h-[50vh]">
          <OathDualSpinner size="lg" />
        </div>
      </OathPageWrapper>
    );
  }

  if (loadError) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <OathContentCard className="text-center py-8">
          <p className="text-red-600 dark:text-red-400 mb-4">{loadError}</p>
          <OathButton variant="outline" onClick={handleBack}>
            Back to Admin
          </OathButton>
        </OathContentCard>
      </OathPageWrapper>
    );
  }

  // Define tabs
  const tabs = [
    { id: 'details', label: 'User Details', icon: UserIcon },
    { id: 'permissions', label: 'Permissions', icon: ShieldCheckIcon, badge: `${selectedPermissions.length}/${availablePermissions.length}` },
  ];

  return (
    <OathPageWrapper maxWidth="max-w-7xl" responsive>
      {/* Header */}
      <div className="mb-6">
        <OathHeaderCard
          title={isEditingSelf ? 'My Account' : 'Edit User'}
          subtitle={userData?.username || userData?.email}
          icon={UserIcon}
        />
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <OathContentCard>
          {/* Tab Navigation */}
          <AdminTabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={tabs}
          />

          {/* User Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-3">
              {/* Row 1: Executing Agent */}
              {userData?.producerId ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Linked Executing Agent
                  </label>
                  <div className="relative sm:max-w-sm">
                    <IdentificationIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      value={userData.producerName || `Producer #${userData.producerId}`}
                      disabled
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                    />
                  </div>
                </div>
              ) : !isEditingSelf && availableProducers.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Executing Agent <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                  </label>
                  <div className="relative sm:max-w-sm">
                    <IdentificationIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <select
                      value={selectedProducerId}
                      onChange={(e) => setSelectedProducerId(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all dark:bg-gray-700 dark:text-white appearance-none"
                    >
                      <option value="">Not an executing agent</option>
                      {availableProducers.map((p) => (
                        <option key={p.producerId} value={p.producerId}>
                          {p.fullName || `${p.firstName} ${p.lastName}`}{p.isPrimary ? ' (Primary)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Once linked, this cannot be changed
                  </p>
                </div>
              ) : null}

              {/* Row 2: Username + Email (both read-only) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {userData?.username && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      value={userData.username}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-mono"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <input
                      type="email"
                      value={userData?.email || ''}
                      disabled
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: First Name + Last Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    disabled={isEditingSelf}
                    className={`w-full px-3 py-2 border rounded-lg text-sm transition-all dark:text-white dark:placeholder-gray-400 ${
                      isEditingSelf ? 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600' :
                      errors.firstName ? 'border-red-300 bg-red-50/50 dark:border-red-500 dark:bg-red-900/20 focus:ring-2 focus:ring-primary/20 focus:border-primary' : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-primary/20 focus:border-primary'
                    }`}
                    placeholder="First name"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    disabled={isEditingSelf}
                    className={`w-full px-3 py-2 border rounded-lg text-sm transition-all dark:text-white dark:placeholder-gray-400 ${
                      isEditingSelf ? 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600' :
                      errors.lastName ? 'border-red-300 bg-red-50/50 dark:border-red-500 dark:bg-red-900/20 focus:ring-2 focus:ring-primary/20 focus:border-primary' : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:ring-2 focus:ring-primary/20 focus:border-primary'
                    }`}
                    placeholder="Last name"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Row 4: Phone */}
              <div className="sm:max-w-[calc(50%-0.375rem)]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    disabled={isEditingSelf}
                    className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm transition-all dark:placeholder-gray-400 ${
                      isEditingSelf ? 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600' : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary'
                    }`}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>

              {/* Row 4: Active Status + Last Login */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                <label className={`flex items-center gap-2.5 ${isEditingSelf ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      disabled={isEditingSelf}
                      className="sr-only peer"
                    />
                    <div className={`w-9 h-5 rounded-full transition-colors ${
                      formData.isActive ? 'bg-success' : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      formData.isActive ? 'translate-x-4' : ''
                    }`} />
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {isEditingSelf && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">— cannot deactivate yourself</span>
                  )}
                </label>
                {userData?.lastLoginAt && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Last login: {new Date(userData.lastLoginAt).toLocaleDateString()}
                  </span>
                )}
                {errors.isActive && (
                  <p className="text-xs text-red-600 dark:text-red-400">{errors.isActive}</p>
                )}
              </div>
            </div>
          )}

          {/* Permissions Tab */}
          {activeTab === 'permissions' && (
            <div>
              {isEditingSelf && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Your permissions are managed by an administrator.
                  </p>
                </div>
              )}
              {!isEditingSelf && errors.permissions && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.permissions}</p>
                </div>
              )}
              <PermissionSelector
                availablePermissions={availablePermissions}
                selectedPermissions={selectedPermissions}
                inheritedPermissionIds={inheritedPermissionIds}
                onChange={setSelectedPermissions}
                disabled={isEditingSelf}
                maxHeight="100%"
              />
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
            {!isEditingSelf && (
              <div className="mr-auto text-sm text-gray-500 dark:text-gray-400">
                {hasChanges ? (
                  <span className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400">
                    <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
                    Unsaved changes
                  </span>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500 text-xs">No changes</span>
                )}
              </div>
            )}

            <div className="flex gap-2">
              {!isEditingSelf && userData?.forcePasswordChange && (
                <OathButton
                  type="button"
                  variant="outline"
                  onClick={handleResendWelcome}
                  disabled={isSubmitting || isResending}
                  loading={isResending}
                  size="sm"
                >
                  <EnvelopeIcon className="h-3.5 w-3.5" />
                  {isResending ? 'Sending...' : 'Resend Welcome Email'}
                </OathButton>
              )}
              <OathButton
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
                size="sm"
              >
                {isEditingSelf ? 'Back' : 'Cancel'}
              </OathButton>
              {!isEditingSelf && (
                <OathButton
                  type="submit"
                  disabled={isSubmitting || !hasChanges}
                  loading={isSubmitting}
                  size="sm"
                >
                  <CheckIcon className="h-3.5 w-3.5" />
                  {isSubmitting ? 'Saving...' : 'Save'}
                </OathButton>
              )}
            </div>
          </div>
        </OathContentCard>
      </form>
    </OathPageWrapper>
  );
};

export default EditUserPage;
